import React from "react";
import {
  Box,
  Grid,
  GridItem,
  useColorModeValue,
  VStack,
  Text,
  Flex,
  Icon,
} from "@chakra-ui/react";

import MedicalLicenseCard from "../features/profile/MedicalLicens";
import ClinicAddressCard from "../features/profile/ClinicAddressCard";
import AvailabilityCard from "../features/profile/AvailabilityCard";
import {
  InfoOutlineIcon,
  WarningIcon,
  CheckCircleIcon,
} from "@chakra-ui/icons";
import { useAppSelector } from "../hooks/useAppSelector";

import { TDoctorInfo, TClinicAddress, TAvailableTimeSlot } from "../apis/types";
import EditableProfilePicture from "../components/cards/EditableProfilePicture";
import { person } from "../components/defaultHeader/assets";
import PhoneAndTitleCard from "../features/profile/PhoneAndTitleCard";

export type CompletenessStatus = "critical" | "warning" | "complete" | "info";

export interface CompletenessInfo {
  status: CompletenessStatus;
  message: string;
  icon?: React.ElementType;
}

const ProfileSectionWrapper: React.FC<{
  children: React.ReactNode;
  completeness?: CompletenessInfo;
}> = ({ children, completeness }) => {
  const cardBg = useColorModeValue("white", "gray.700");
  const defaultBorderColor = useColorModeValue("gray.200", "gray.600");
  const criticalBorderColor = useColorModeValue("red.300", "red.400");
  const criticalIconColor = useColorModeValue("red.500", "red.400");
  const warningBorderColor = useColorModeValue("orange.300", "orange.400");
  const warningIconColor = useColorModeValue("orange.500", "orange.400");
  const completeBorderColor = useColorModeValue("green.300", "green.400");
  const completeIconColor = useColorModeValue("green.500", "green.400");
  const infoIconColor = useColorModeValue("gray.500", "gray.400");
  const messageTextColor = useColorModeValue("gray.600", "gray.300");

  let currentBorderColor = defaultBorderColor;
  let currentIconColor = infoIconColor;
  let IconComponent = InfoOutlineIcon;

  if (completeness) {
    switch (completeness.status) {
      case "critical":
        currentBorderColor = criticalBorderColor;
        currentIconColor = criticalIconColor;
        IconComponent = WarningIcon;
        break;
      case "warning":
        currentBorderColor = warningBorderColor;
        currentIconColor = warningIconColor;
        IconComponent = InfoOutlineIcon;
        break;
      case "complete":
        currentBorderColor = completeBorderColor;
        currentIconColor = completeIconColor;
        IconComponent = CheckCircleIcon;
        break;
      default:
        IconComponent = InfoOutlineIcon;
        break;
    }
  }

  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      boxShadow="md"
      p={{ base: 4, md: 6 }}
      w="100%"
      h="100%"
      borderWidth="1px"
      borderColor={currentBorderColor}
      position="relative"
    >
      {children}
      {completeness && completeness.message && (
        <Flex
          alignItems="center"
          mt={4}
          p={2}
          borderTopWidth="1px"
          borderColor={defaultBorderColor}
        >
          <Icon
            as={completeness.icon || IconComponent}
            color={currentIconColor}
            mr={2}
          />
          <Text fontSize="sm" color={messageTextColor}>
            {completeness.message}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

const ProfilePage = () => {
  const containerBg = useColorModeValue("gray.50", "gray.800");
  const { doctorInfo } = useAppSelector((state) => state.auth);

  const [showContactForm, setShowContactForm] = React.useState(false);

  const calculateMedicalLicenseCompleteness = (): CompletenessInfo => {
    if (!doctorInfo)
      return { status: "info", message: "Loading doctor information..." };

    const importantFields = [
      {
        key: "licenseNumber",
        label: "Practicing License Number",
        isFilled: !!doctorInfo.licenseNumber,
      },
      {
        key: "specialty",
        label: "Specialties",
        isFilled: !!(doctorInfo.specialty && doctorInfo.specialty.length > 0),
      },
    ];

    const filledCount = importantFields.filter((f) => f.isFilled).length;
    const totalImportantCount = importantFields.length;
    const missingCount = totalImportantCount - filledCount;

    if (missingCount === 0) {
      return {
        status: "complete",
        message: "Medical license details are complete.",
        icon: CheckCircleIcon,
      };
    }
    if (
      !doctorInfo.licenseNumber ||
      !(doctorInfo.specialty && doctorInfo.specialty.length > 0)
    ) {
      return {
        status: "critical",
        message: `Medical license section is critically incomplete. ${missingCount} of ${totalImportantCount} important fields missing.`,
        icon: WarningIcon,
      };
    }
    return {
      status: "warning",
      message: `Medical license section is incomplete. ${missingCount} of ${totalImportantCount} important fields missing.`,
      icon: InfoOutlineIcon,
    };
  };

  const calculateClinicAddressCompleteness = (): CompletenessInfo => {
    if (!doctorInfo) return { status: "info", message: "Loading..." };

    const addresses: TClinicAddress[] = doctorInfo.clinics || [];
    const hasAtLeastOneAddress = addresses.length > 0;
    const isAnyAddressSufficient = addresses.some(
      (addr) => addr.address && addr.city && addr.state
    );

    if (hasAtLeastOneAddress && isAnyAddressSufficient) {
      return {
        status: "complete",
        message: "Clinic address information is complete.",
      };
    }
    if (hasAtLeastOneAddress && !isAnyAddressSufficient) {
      return {
        status: "warning",
        message:
          "One or more addresses are incomplete. Please fill in all required fields for each address.",
      };
    }
    return {
      status: "critical",
      message: "At least one complete clinic address is required.",
    };
  };

  const calculateAvailabilityCompleteness = (): CompletenessInfo => {
    if (!doctorInfo) return { status: "info", message: "Loading..." };

    const availabilitySlots: TAvailableTimeSlot[] =
      doctorInfo.availability || [];
    const hasAtLeastOneSlot = availabilitySlots.length > 0;
    const areAllSlotsComplete = availabilitySlots.every(
      (slot) => slot.day && slot.startTime && slot.endTime
    );

    if (hasAtLeastOneSlot && areAllSlotsComplete) {
      return {
        status: "complete",
        message: "Availability is set and complete.",
      };
    }
    if (hasAtLeastOneSlot && !areAllSlotsComplete) {
      return {
        status: "warning",
        message:
          "One or more availability slots are incomplete. Please fill in day, start time, and end time for all entries.",
      };
    }
    return {
      status: "critical",
      message: "Availability is not set. Please add your available time slots.",
    };
  };

  const medicalLicenseCompleteness = calculateMedicalLicenseCompleteness();
  const clinicAddressCompleteness = calculateClinicAddressCompleteness();
  const availabilityCompleteness = calculateAvailabilityCompleteness();

  return (
    <Box
      bg={containerBg}
      minH="calc(100vh - 80px)"
      py={{ base: 4, md: 8 }}
      px={{ base: 2, md: 4 }}
    >
      <VStack spacing={8} maxW="container.xl" mx="auto">
        {/* <ProfileHeader /> */}
        <EditableProfilePicture
          doctorId={doctorInfo._id}
          currentImage={doctorInfo?.profilePicture || person}
        />

        <Grid
          templateColumns={{ base: "repeat(1, 1fr)", lg: "repeat(2, 1fr)" }}
          gap={6}
          w="100%"
        >
          <GridItem>
            <ProfileSectionWrapper completeness={medicalLicenseCompleteness}>
              <MedicalLicenseCard />
            </ProfileSectionWrapper>
          </GridItem>

          <GridItem>
            <ProfileSectionWrapper completeness={clinicAddressCompleteness}>
              <ClinicAddressCard />
            </ProfileSectionWrapper>
          </GridItem>

          <GridItem>
            <ProfileSectionWrapper completeness={availabilityCompleteness}>
              <AvailabilityCard />
            </ProfileSectionWrapper>
          </GridItem>
          <GridItem>
            <ProfileSectionWrapper>
              <PhoneAndTitleCard />
            </ProfileSectionWrapper>
          </GridItem>
        </Grid>
      </VStack>
    </Box>
  );
};

export default ProfilePage;
